import React, { Suspense, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate, useMatches } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useAcademy } from '../../hooks/useAcademy';
import { useMyPlayer } from '../../hooks/useMyPlayer';
import { signOut } from '../../firebase/auth';
import db, { doc, updateDoc, serverTimestamp } from '../../firebase/firestore';
import PageLoadingFallback from '../shared/PageLoadingFallback';
import LanguageChip from '../shared/LanguageChip';
import {
  Squares2X2Icon,
  UserCircleIcon,
  BuildingLibraryIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  AcademicCapIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';

const BG = '#05070c';
const PANEL = '#0b0f17';
const BORDER = '#1c2333';
const ACCENT = '#2dd4bf';
const TEXT_MUTED = '#8993a6';

function getInitials(displayName, email) {
  if (displayName) return displayName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  return (email?.[0] ?? '?').toUpperCase();
}

const NAV = [
  { to: '/plyrmatch/dashboard', labelKey: 'plyrmatch:nav.dashboard', icon: Squares2X2Icon },
  { to: '/plyrmatch/profile',   labelKey: 'plyrmatch:nav.playerProfile', icon: UserCircleIcon },
  { to: '/plyrmatch/stats',     labelKey: 'plyrmatch:nav.stats', icon: ChartBarIcon },
  { to: '/plyrmatch/academies', labelKey: 'plyrmatch:nav.academyHub', icon: BuildingLibraryIcon },
  { to: '/plyrmatch/mentorship', labelKey: 'common:routeTitles.mentorship', icon: AcademicCapIcon },
  { to: '/plyrmatch/scenarios', labelKey: 'plyrmatch:nav.scenarios', icon: LightBulbIcon },
  { to: '/plyrmatch/next-steps', labelKey: 'plyrmatch:nav.nextSteps', icon: ClipboardDocumentCheckIcon },
  { to: '/plyrmatch/messages',  labelKey: 'common:routeTitles.messages', icon: ChatBubbleLeftRightIcon },
];

function SidebarContent({ onNavigate }) {
  const { t } = useTranslation();
  const { userProfile, user } = useAuth();
  const { academy } = useAcademy();
  const { player } = useMyPlayer();
  const initials = getInitials(userProfile?.displayName, user?.email);
  // Once a player has a destination (a real placement or just a country tag
  // while waiting on one), browsing other academies no longer makes sense —
  // see PlyrmatchAcademyHub's own redirect for the direct-URL case.
  const isSettled = !!(player?.academyId || player?.assignedCountry);
  const nav = NAV.filter((item) => (item.to !== '/plyrmatch/academies' || !isSettled) && (item.to !== '/plyrmatch/mentorship' || !!player?.mentorshipStatus || !!player?.mentorshipQuestionnaire));

  return (
    <div className="flex flex-col h-full" style={{ background: PANEL, color: '#e5e7eb' }}>
      <div className="px-5 pt-6 pb-4">
        <p className="text-base font-extrabold tracking-tight text-white">{academy?.name?.toUpperCase() || 'PLYRMATCH'}</p>
        <p className="text-xs" style={{ color: TEXT_MUTED }}>{t('plyrmatch:shell.playerPortal', 'Player Portal')}</p>
      </div>

      <div className="px-5 pb-4">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: ACCENT, color: '#052e2b' }}>
            {initials}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{userProfile?.displayName || t('plyrmatch:shell.player', 'Player')}</p>
            <p className="text-[11px] truncate" style={{ color: TEXT_MUTED }}>{user?.email}</p>
          </div>
        </div>
      </div>

      <div className="px-3 pt-2 pb-3">
        <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-wider" style={{ color: TEXT_MUTED }}>
          {t('plyrmatch:shell.navigation', 'Navigation')}
        </p>
        <nav className="space-y-0.5">
          {nav.map(({ to, labelKey, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors`
              }
              style={({ isActive }) => ({
                background: isActive ? 'rgba(45,212,191,0.12)' : 'transparent',
                color: isActive ? ACCENT : '#cbd5e1',
              })}
            >
              <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
              {t(labelKey)}
            </NavLink>
          ))}
        </nav>
      </div>

      <div className="mt-auto px-4 pb-5 pt-3" style={{ borderTop: `1px solid ${BORDER}` }}>
        <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: TEXT_MUTED }}>
          {t('plyrmatch:shell.contactPrefix', 'Contact {{name}}', { name: academy?.name || 'PLYRMATCH' })}
        </p>
        {academy?.contactEmail && <p className="text-xs" style={{ color: '#cbd5e1' }}>{academy.contactEmail}</p>}
      </div>
    </div>
  );
}

// Top-right initials button — Settings, Switch portal, and Sign out used to
// live as standing buttons in the sidebar; moved behind a click here so the
// sidebar stays just navigation + identity.
function UserMenu() {
  const { t } = useTranslation();
  const { userProfile, user, access } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const initials = getInitials(userProfile?.displayName, user?.email);
  const canSwitchPortal = Array.isArray(access) && access.length > 1;

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
        style={{ background: ACCENT, color: '#052e2b' }}
        title={userProfile?.displayName || user?.email}
      >
        {initials}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-56 rounded-xl py-1.5 z-50 shadow-lg"
          style={{ background: PANEL, border: `1px solid ${BORDER}` }}
        >
          <div className="px-3.5 py-2" style={{ borderBottom: `1px solid ${BORDER}` }}>
            <p className="text-sm font-semibold text-white truncate">{userProfile?.displayName || t('plyrmatch:shell.player', 'Player')}</p>
            <p className="text-[11px] truncate" style={{ color: TEXT_MUTED }}>{user?.email}</p>
          </div>
          <button
            onClick={() => { navigate('/plyrmatch/settings'); setOpen(false); }}
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-left"
            style={{ color: '#cbd5e1' }}
          >
            <Cog6ToothIcon className="h-4 w-4" /> {t('common:actions.settings', 'Settings')}
          </button>
          {canSwitchPortal && (
            <button
              onClick={() => { navigate('/portal/choose'); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-left"
              style={{ color: '#cbd5e1' }}
            >
              {t('common:actions.switchPortal', 'Switch portal')}
            </button>
          )}
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-left"
            style={{ color: '#f87171' }}
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4" /> {t('common:actions.signOut', 'Log Out')}
          </button>
        </div>
      )}
    </div>
  );
}

export default function PlyrmatchPortalShell() {
  const matches = useMatches();
  const title = matches.at(-1)?.handle?.title;
  const { t } = useTranslation();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  // "Last active in the PLYRMATCH Portal" for agency staff (see
  // getPlyrmatchPortalStatus) — the login's own last-active time can't tell
  // this portal apart from an academy's portal on the same account.
  // Also on returning to the tab/app (a home-screen install can stay open
  // for days), at most hourly.
  useEffect(() => {
    if (!user?.uid) return undefined;
    let lastWrite = 0;
    const touch = () => {
      if (document.visibilityState !== 'visible' || Date.now() - lastWrite < 60 * 60 * 1000) return;
      lastWrite = Date.now();
      updateDoc(doc(db, 'users', user.uid), { plyrmatchPortalLastOpenedAt: serverTimestamp() })
        .catch(() => { /* legacy account with no users doc — tracking only */ });
    };
    touch();
    document.addEventListener('visibilitychange', touch);
    return () => document.removeEventListener('visibilitychange', touch);
  }, [user?.uid]);

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: BG }}>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <aside className="hidden md:block w-64 flex-shrink-0" style={{ borderRight: `1px solid ${BORDER}` }}>
        <SidebarContent />
      </aside>

      {mobileOpen && (
        <aside className="fixed inset-y-0 left-0 z-50 w-72 md:hidden" style={{ borderRight: `1px solid ${BORDER}` }}>
          <SidebarContent onNavigate={() => setMobileOpen(false)} />
        </aside>
      )}

      <div className="flex flex-col flex-1 overflow-hidden">
        <header className="md:hidden flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${BORDER}` }}>
          <button onClick={() => setMobileOpen(true)} style={{ color: '#e5e7eb' }}>
            <Bars3Icon className="h-6 w-6" />
          </button>
          <p className="text-sm font-bold text-white">{title ? t(title) : 'PLYRMATCH'}</p>
          <div className="flex items-center gap-2.5">
            <LanguageChip variant="tailwind" />
            <UserMenu />
          </div>
        </header>
        <div className="hidden md:flex items-center justify-end gap-3 px-8 pt-5 pb-1">
          <LanguageChip variant="tailwind" />
          <UserMenu />
        </div>
        <main className="flex-1 overflow-y-auto px-4 py-4 md:px-8 md:py-6">
          <Suspense fallback={<PageLoadingFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {mobileOpen && (
        <button
          className="fixed top-3 right-3 z-50 md:hidden text-white"
          onClick={() => setMobileOpen(false)}
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}
